import React, {useEffect, useState} from "react";
import {Helmet} from "react-helmet";
import {Link} from "react-router-dom";
import api from "../api";
import V2Wordmark from "./V2Wordmark";
import "./V2Home.css";
import "./V2Menu.css";
import "./V2Admin.css";

/**
 * V2Admin — operator dashboard.
 *
 * Three responsibilities:
 *   1. Live status toggle  (POST /api/online/{on,off})
 *   2. Stats overview      (GET /api/stats)
 *   3. Recent orders list  (GET /api/orders, lazy-loaded)
 *
 * Soft auth via REACT_APP_ADMIN_PASSWORD. NOT REAL SECURITY — anyone
 * reading the JS bundle can find the password. This is purely to stop
 * casual URL-guessing. Real protection lives server-side.
 */
const ADMIN_PW =
  process.env.REACT_APP_ADMIN_PASSWORD || "chinad-admin"; // dev fallback
const SESSION_KEY = "v2_admin_authed";

function V2Admin() {
  const [authed, setAuthed] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === "1"
  );

  if (!authed) {
    return <PasswordGate onAuthed={() => {
      sessionStorage.setItem(SESSION_KEY, "1");
      setAuthed(true);
    }} />;
  }
  return <AdminDashboard onLogout={() => {
    sessionStorage.removeItem(SESSION_KEY);
    setAuthed(false);
  }} />;
}

/* ============================================================
   PASSWORD GATE
   ============================================================ */
function PasswordGate({onAuthed}) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (pw === ADMIN_PW) onAuthed();
    else setError("Wrong password.");
  };

  return (
    <div className="v2-root v2-menu-root v2-admin-root">
      <Helmet><title>Admin — China Delight</title></Helmet>
      <div className="v2-container v2-stickybar">
        <nav className="v2-nav">
          <V2Wordmark to="/" />
        </nav>
      </div>
      <main className="v2-container v2-admin-gate">
        <h1 className="v2-hero-title"><em>Admin</em></h1>
        <p className="v2-menu-lede">Enter the admin password to continue.</p>
        <form className="v2-admin-gate-form" onSubmit={submit}>
          <input
            type="password"
            placeholder="Password"
            value={pw}
            onChange={(e) => { setPw(e.target.value); setError(""); }}
            autoFocus
          />
          {error && <div className="v2-form-error">{error}</div>}
          <button type="submit" className="v2-cta-primary">Enter →</button>
        </form>
      </main>
    </div>
  );
}

/* ============================================================
   DASHBOARD
   ============================================================ */
function AdminDashboard({onLogout}) {
  const [online, setOnline] = useState(null);    // server-side live flag
  const [toggling, setToggling] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsErr, setStatsErr] = useState("");
  const [orders, setOrders] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersErr, setOrdersErr] = useState("");

  // initial load: status + stats (skip orders — lazy)
  useEffect(() => {
    api.get("/api/online")
      .then((r) => setOnline(r.data !== false && r.data !== "false"))
      .catch(() => setOnline(null));
    api.get("/api/stats")
      .then((r) => setStats(r.data))
      .catch((e) => setStatsErr(e?.message || "Failed to load stats"));
  }, []);

  const toggleOnline = async () => {
    if (toggling || online === null) return;
    setToggling(true);
    try {
      const path = online ? "/api/online/off" : "/api/online/on";
      await api.post(path);
      setOnline(!online);
    } catch (e) {
      alert("Could not toggle live status. " + (e?.message || ""));
    } finally {
      setToggling(false);
    }
  };

  const loadOrders = async () => {
    setOrdersLoading(true);
    setOrdersErr("");
    try {
      const r = await api.get("/api/orders");
      // server returns most-recent-first usually; trust the order
      setOrders(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setOrdersErr("Could not load orders. The endpoint may be slow or down.");
    } finally {
      setOrdersLoading(false);
    }
  };

  const fmt = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });

  // pull yearly totals from /api/stats response
  const totalOrders = stats?.count?.all ?? null;
  const yearKeys = stats?.count
    ? Object.keys(stats.count).filter((k) => k.startsWith("y"))
    : [];
  const latestYearKey = yearKeys.sort().pop();
  const latestYear = latestYearKey ? stats.count[latestYearKey] : null;

  return (
    <div className="v2-root v2-menu-root v2-admin-root">
      <Helmet><title>Admin — China Delight</title></Helmet>

      <div className="v2-container v2-stickybar">
        <nav className="v2-nav">
          <V2Wordmark to="/" />
          <div className="v2-nav-links v2-nav-links--desktop">
            <Link to="/menu">Menu</Link>
            <button className="v2-admin-logout" onClick={onLogout}>Sign out</button>
          </div>
        </nav>
      </div>

      <main className="v2-container v2-admin">
        <header className="v2-admin-header">
          <div className="v2-eyebrow">
            <span className="v2-eyebrow-rule" />
            Operator dashboard
          </div>
          <h1 className="v2-hero-title"><em>Admin</em></h1>
        </header>

        {/* === STATUS TOGGLE === */}
        <section className="v2-admin-card v2-admin-status">
          <div>
            <h2>Live status</h2>
            <p className="v2-admin-card-sub">
              Toggle off to stop accepting online orders. Closes the
              checkout button across all v2 pages within ~2 minutes.
            </p>
          </div>
          <div className="v2-admin-status-control">
            <span className={`v2-admin-dot ${online ? "is-on" : "is-off"}`} />
            <span className="v2-admin-status-label">
              {online === null ? "Loading…" : online ? "Live" : "Offline"}
            </span>
            <button
              type="button"
              className={`v2-admin-toggle ${online ? "is-on" : "is-off"}`}
              onClick={toggleOnline}
              disabled={online === null || toggling}
              aria-pressed={online ? "true" : "false"}
            >
              <span className="v2-admin-toggle-thumb" />
            </button>
          </div>
        </section>

        {/* === STATS === */}
        <section className="v2-admin-card">
          <h2>Order stats</h2>
          {statsErr && <div className="v2-form-error">{statsErr}</div>}
          {!stats && !statsErr && <p className="v2-admin-card-sub">Loading…</p>}
          {stats && (
            <div className="v2-admin-stat-grid">
              <Stat label="Total orders, all-time" value={totalOrders ?? "—"} />
              <Stat
                label={latestYearKey ? `Total orders in ${latestYearKey.slice(1)}` : "Latest year"}
                value={latestYear?.total ?? "—"}
              />
              {latestYear?.days && (
                <Stat
                  label="Busiest day this year"
                  value={busiestKey(latestYear.days)}
                />
              )}
              {latestYear?.hours && (
                <Stat
                  label="Busiest hour this year"
                  value={busiestHour(latestYear.hours)}
                />
              )}
            </div>
          )}

          {stats?.count && (
            <details className="v2-admin-details">
              <summary>Per-year breakdown</summary>
              <table className="v2-admin-table v2-admin-table--years">
                <thead>
                  <tr><th>Year</th><th>Orders</th></tr>
                </thead>
                <tbody>
                  {yearKeys.sort().reverse().map((k) => (
                    <tr key={k}>
                      <td>{k.slice(1)}</td>
                      <td>{stats.count[k]?.total?.toLocaleString() ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </section>

        {/* === RECENT ORDERS === lazy-load to avoid the slow endpoint */}
        <section className="v2-admin-card">
          <div className="v2-admin-orders-head">
            <div>
              <h2>Recent orders</h2>
              <p className="v2-admin-card-sub">
                Pulls from <code>/api/orders</code>. The endpoint can be slow
                — load on demand.
              </p>
            </div>
            <button
              className="v2-cta-primary"
              onClick={loadOrders}
              disabled={ordersLoading}
            >
              {ordersLoading ? "Loading…" : orders ? "Refresh" : "Load orders"}
            </button>
          </div>

          {ordersErr && <div className="v2-form-error">{ordersErr}</div>}
          {orders && orders.length === 0 && (
            <p className="v2-admin-card-sub">No orders yet.</p>
          )}
          {orders && orders.length > 0 && (
            <div className="v2-admin-orders-table-wrap">
              <table className="v2-admin-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.slice(0, 50).map((o, i) => (
                    <tr key={o._id || i}>
                      <td>{formatTime(o.timePlaced)}</td>
                      <td>{o.name || "—"}</td>
                      <td>{o.phone || "—"}</td>
                      <td className="v2-admin-items">
                        {(o.cart || []).map((it, j) => (
                          <div key={j}>
                            {it.quantity}× {it.title}
                          </div>
                        ))}
                      </td>
                      <td>{fmt.format(o.total || 0)}</td>
                      <td>{o.paymentMethod || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {orders.length > 50 && (
                <p className="v2-admin-card-sub">
                  Showing the first 50 of {orders.length}.
                </p>
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

/* ---- helpers ---- */

function Stat({label, value}) {
  return (
    <div className="v2-admin-stat">
      <div className="v2-admin-stat-value">{typeof value === "number" ? value.toLocaleString() : value}</div>
      <div className="v2-admin-stat-label">{label}</div>
    </div>
  );
}

function busiestKey(obj) {
  let bestKey = "—";
  let bestVal = -1;
  for (const [k, v] of Object.entries(obj)) {
    if (v > bestVal) { bestVal = v; bestKey = k; }
  }
  return `${bestKey} (${bestVal})`;
}

function busiestHour(hours) {
  // hours keys look like "h17" → 5 PM
  let bestKey = "—";
  let bestVal = -1;
  for (const [k, v] of Object.entries(hours)) {
    if (v > bestVal) { bestVal = v; bestKey = k; }
  }
  if (bestKey === "—") return bestKey;
  const h = parseInt(bestKey.slice(1), 10);
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12} ${ap} (${bestVal})`;
}

function formatTime(timestamp) {
  if (!timestamp) return "—";
  try {
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return timestamp;
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return timestamp;
  }
}

export default V2Admin;
